import React from "react";
import clsx from "clsx";
import { isEmpty } from "lodash";
import { useFormContext } from "react-hook-form";

import { HookFormInputGroup } from "components";
import { FormColumn, FormRow } from "components/layout";
import { PaymentMethodValues } from "types";
import {
	creditCardExpDateNormalizer,
	trimMidSentenceNormalizer,
	trimNormalizer,
} from "validators";
import {
	matchesNameRegex,
	maxLength150,
	maxLength4,
	minLength2,
	minLength3,
	normalizeAll,
	onlyAlphanumericAndSpecialCharacters,
	onlyDigits,
	onlyMaxAlphaNumeric4,
	required,
	validExpDate,
} from "validators/validation";

import CreditCardNumber from "./creditCardNumber";
import { getModalInputStyles } from "./getModalStyles";
import {
	cardHolderNameFieldName,
	customerIdFieldName,
	cvvFieldName,
	expirationDateFieldName,
	payeeFirstNameFieldName,
	payeeLastNameFieldName,
} from "./paymentMethodConstants";

import styles from "./paymentMethod.module.scss";

type CreditCardFieldsProps = {
	isLoading: boolean;
	defaultValues: PaymentMethodValues | any;
	isModal: boolean;
};

export const CreditCardFields = ({
	isLoading,
	defaultValues,
	isModal,
}: CreditCardFieldsProps) => {
	const {
		register,
		formState: { errors },
	} = useFormContext();

	const isCreditCardCustomerId = !isEmpty(defaultValues?.customerId);

	const inputStyle = getModalInputStyles(isModal);

	return (
		<>
			<FormRow>
				<FormColumn
					className={clsx(isModal && styles.modalFormColumnFull)}
				>
					<HookFormInputGroup
						name={customerIdFieldName}
						register={register}
						hidden
					/>
					<HookFormInputGroup
						colLabel={inputStyle}
						colInput={inputStyle}
						name={cardHolderNameFieldName}
						register={register}
						label="Cardholder Name"
						blurLoading={isLoading}
						autoComplete="off"
						normalizeOnBlur={trimNormalizer}
						errors={errors}
						validator={{
							required,
							minLength2,
							maxLength150,
							matchesNameRegex,
						}}
						normalize={normalizeAll([
							onlyAlphanumericAndSpecialCharacters,
							trimMidSentenceNormalizer,
						])}
						showRequiredMark
						disabled={isCreditCardCustomerId}
						isPlainText={isCreditCardCustomerId}
					/>
				</FormColumn>
				<FormColumn
					className={clsx(isModal && styles.modalFormColumnFull)}
				>
					<CreditCardNumber
						isLoading={isLoading}
						defaultValues={defaultValues}
						isModal={isModal}
					/>
				</FormColumn>
			</FormRow>
			<FormRow>
				<FormColumn
					className={clsx(isModal && styles.modalFormColumnHalf)}
				>
					<HookFormInputGroup
						colLabel={inputStyle}
						colInput={inputStyle}
						name={expirationDateFieldName}
						register={register}
						label="Expiration Date"
						blurLoading={isLoading}
						autoComplete="off"
						normalizeOnBlur={creditCardExpDateNormalizer}
						errors={errors}
						validator={{ required, validExpDate }}
						normalize={normalizeAll([creditCardExpDateNormalizer])}
						placeholder="MM/YY"
						showRequiredMark
						disabled={isCreditCardCustomerId}
						isPlainText={isCreditCardCustomerId}
					/>
				</FormColumn>
				<FormColumn
					className={clsx(isModal && styles.modalFormColumnHalf)}
				>
					<HookFormInputGroup
						colLabel={inputStyle}
						colInput={inputStyle}
						name={cvvFieldName}
						register={register}
						label="CVV"
						type={!defaultValues?.cvv ? "password" : "text"}
						blurLoading={isLoading}
						autoComplete="off"
						normalizeOnBlur={trimNormalizer}
						errors={errors}
						validator={{ required, minLength3, maxLength4 }}
						normalize={normalizeAll([
							onlyDigits,
							onlyMaxAlphaNumeric4,
						])}
						showRequiredMark
						disabled={isCreditCardCustomerId}
						isPlainText={isCreditCardCustomerId}
					/>
				</FormColumn>
			</FormRow>
			<FormRow>
				<FormColumn
					className={clsx(isModal && styles.modalFormColumnHalf)}
				>
					<HookFormInputGroup
						colLabel={inputStyle}
						colInput={inputStyle}
						name={payeeFirstNameFieldName}
						register={register}
						label="Payee First Name"
						blurLoading={isLoading}
						autoComplete="off"
						normalizeOnBlur={trimNormalizer}
						errors={errors}
						validator={{
							required,
							minLength2,
							maxLength150,
							matchesNameRegex,
						}}
						normalize={normalizeAll([
							onlyAlphanumericAndSpecialCharacters,
							trimMidSentenceNormalizer,
						])}
						showRequiredMark
						isPlainText={isCreditCardCustomerId}
						disabled={isCreditCardCustomerId}
					/>
				</FormColumn>
				<FormColumn
					className={clsx(isModal && styles.modalFormColumnHalf)}
				>
					<HookFormInputGroup
						colLabel={inputStyle}
						colInput={inputStyle}
						name={payeeLastNameFieldName}
						register={register}
						label="Payee Last Name"
						blurLoading={isLoading}
						autoComplete="off"
						normalizeOnBlur={trimNormalizer}
						errors={errors}
						validator={{
							required,
							minLength2,
							maxLength150,
							matchesNameRegex,
						}}
						normalize={normalizeAll([
							onlyAlphanumericAndSpecialCharacters,
							trimMidSentenceNormalizer,
						])}
						showRequiredMark
						isPlainText={isCreditCardCustomerId}
						disabled={isCreditCardCustomerId}
					/>
				</FormColumn>
			</FormRow>
		</>
	);
};
